import { type FC } from 'react';
import { Link } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faArrowRight,
  faArrowLeft,
  faHeartPulse,
} from '@fortawesome/free-solid-svg-icons';
import PublicNavBar from '../../components/layout/PublicNavBar';
import PipelineStrip from '../../components/layout/PipelineStrip';
import PublicFooter from '../../components/layout/PublicFooter';
import ConsolePanel from './shared/ConsolePanel';
import AdaptiveFlowHeroMock from './continuous-validation/AdaptiveFlowHeroMock';
import ReadinessChecksMock from './continuous-validation/ReadinessChecksMock';
import LiveProgressMock from './continuous-validation/LiveProgressMock';
import EquityCurveRiskMock from './continuous-validation/EquityCurveRiskMock';
import RightSidebarMock from './continuous-validation/RightSidebarMock';
import TradesMonitoringMock from './continuous-validation/TradesMonitoringMock';
import ReOptApprovalMock from './continuous-validation/ReOptApprovalMock';
import { usePageMeta } from '../../hooks/usePageMeta';

/* ═══════════════════════════ PAGE ═════════════════════════════════════ */

const ContinuousValidationPage: FC = () => {
  usePageMeta({ title: 'Continuous Validation', description: 'Monitor deployed strategies with Adaptive Flow. Continuous performance evaluation, parameter stability tracking, and structural drift detection.' });

  return (
    <div className="min-h-screen bg-[#0a0e14] text-gray-100 font-[Poppins] selection:bg-[#9d4edd]/30">
      <PublicNavBar />

      {/* ════════════════ 1. HERO ════════════════ */}
      <section className="relative pt-24 sm:pt-36 pb-28 px-4 sm:px-6 lg:px-10 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-[#0f1520] via-[#0a0e14] to-[#080b11]" />
        <div className="absolute inset-0 opacity-[0.03] pointer-events-none" style={{ backgroundImage: 'radial-gradient(circle, rgba(255,255,255,0.15) 1px, transparent 1px)', backgroundSize: '24px 24px' }} />

        <div className="max-w-[1440px] mx-auto relative z-10 grid grid-cols-1 lg:grid-cols-[5fr_7fr] gap-12 lg:gap-16 items-center">
          {/* Left -- copy */}
          <div>
            <p className="text-[10px] font-mono tracking-[0.2em] uppercase text-[#9d4edd]/60 mb-5">
              03 &mdash; Continuous Validation
            </p>

            <h1 className="text-3xl sm:text-[2.25rem] md:text-[2.5rem] font-bold leading-[1.1] tracking-[-0.025em] mb-4 text-white">
              Continuous validation for deployed strategies
            </h1>

            <p className="text-[15px] text-gray-300/70 leading-[1.7] mb-3 max-w-[500px]">
              Adaptive Flow monitors deployed strategies using the same
              validation framework used during research, continuously
              evaluating performance, parameter stability, and structural
              drift.
            </p>

            <p className="text-[14px] text-white/60 italic mb-5">
              Validation does not end when a strategy is deployed.
            </p>

            <p className="text-[12px] font-mono uppercase tracking-widest text-gray-500 mb-2">The system monitors</p>
            <div className="space-y-2 mb-5">
              {[
                'Live performance compared against validation baseline',
                'Stability monitoring across deployed parameters',
                'Automatic detection of performance degradation',
                'Controlled re-optimization workflow',
              ].map((item, i) => (
                <div key={i} className="flex items-start gap-2.5">
                  <span className="w-1 h-1 rounded-full bg-[#9d4edd]/50 mt-[7px] flex-shrink-0" />
                  <span className="text-[13px] text-gray-400 leading-snug">{item}</span>
                </div>
              ))}
            </div>

            <div className="flex flex-col sm:flex-row items-center gap-4 mb-5">
              <Link
                to="/pricing"
                className="inline-flex items-center gap-2.5 px-7 py-3 bg-[#9d4edd] hover:bg-[#8b3ec7] text-white rounded text-sm font-medium transition-colors"
              >
                Start Research
                <FontAwesomeIcon icon={faArrowRight} className="text-xs" />
              </Link>
              <a href="#how-it-works" className="text-[13px] text-gray-400 hover:text-gray-200 transition-colors">
                How it works <span className="text-gray-600">&rarr;</span>
              </a>
            </div>

            <p className="text-[12px] text-gray-500 leading-snug max-w-[440px]">
              Outputs: health score, confidence level, deviation alerts, re-optimization triggers, and live performance envelopes.
            </p>
          </div>

          {/* Right -- Hero visual */}
          <AdaptiveFlowHeroMock />
        </div>
      </section>

      <PipelineStrip active="monitoring" />

      {/* ════════════════ 2. HOW IT WORKS ════════════════ */}
      <section id="how-it-works" className="py-16 px-4 sm:px-6 lg:px-10 bg-[#F9FAFB]">
        <div className="max-w-[960px] mx-auto">
          <div className="text-center mb-14">
            <h2 className="text-xl md:text-[24px] font-bold text-gray-950 tracking-[-0.015em] mb-5">
              Adaptive Flow lifecycle
            </h2>
            <p className="text-[15px] text-gray-600 leading-[1.7] max-w-[600px] mx-auto">
              Once a strategy passes research validation, Adaptive Flow begins
              continuous monitoring through a structured lifecycle.
            </p>
          </div>

          {/* Lifecycle stages */}
          <div className="space-y-4">
            {[
              {
                num: '01',
                title: 'Readiness Assessment',
                desc: 'Pre-launch verification that WFA passed, stability thresholds are met, sample adequacy is confirmed, and risk classification is complete.',
                output: 'Go/no-go decision for live validation',
                decision: 'All checks must pass to enable start',
              },
              {
                num: '02',
                title: 'Collecting & Warmup',
                desc: 'Live price feed connects. Candles accumulate. Statistical baseline forms. The 2-of-3 warmup rule requires sufficient trades, candles, and calendar days before confidence readings stabilise.',
                output: 'Statistical baseline for comparison',
                decision: 'Confidence must reach minimum threshold',
              },
              {
                num: '03',
                title: 'Active Monitoring',
                desc: 'Live metrics compared against expected envelopes. Sharpe drift, drawdown expansion, performance decay, and stability breach detection run continuously. Health score and confidence update every candle.',
                output: 'Deviation alerts and health score trends',
                decision: 'Warning / Degrading triggers appear when thresholds breached',
              },
              {
                num: '04',
                title: 'Re-optimization Trigger',
                desc: 'When degradation is confirmed, the system surfaces a re-optimization recommendation. A compliance-gated approval modal records the decision. New parameters are optimized and the cycle restarts.',
                output: 'Re-optimized parameters with updated envelope',
                decision: 'Approve / Defer / Reject with compliance audit trail',
              },
            ].map((stage) => (
              <div
                key={stage.num}
                className="flex gap-5 items-start py-5 px-6 rounded-lg border border-gray-200/80 bg-white shadow-sm"
              >
                <div className="flex-shrink-0 w-10 h-10 rounded-lg bg-[#f3e8ff] flex items-center justify-center">
                  <span className="font-mono text-[11px] text-[#9d4edd] font-semibold">{stage.num}</span>
                </div>
                <div className="flex-1 min-w-0">
                  <h3 className="text-[14px] font-semibold text-gray-950 mb-1">{stage.title}</h3>
                  <p className="text-[13px] text-gray-500 leading-[1.6] mb-2">{stage.desc}</p>
                  <p className="text-[12px] text-gray-400 font-mono mb-1">Output: <span className="italic">{stage.output}</span></p>
                  <p className="text-[11px] text-[#9d4edd]/70 font-mono">Gate: {stage.decision}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ════════════════ 3. READINESS CHECKS ════════════════ */}
      <section className="py-16 px-4 sm:px-6 lg:px-10 bg-white">
        <div className="max-w-[1440px] mx-auto grid grid-cols-1 lg:grid-cols-[5fr_7fr] gap-12 lg:gap-16 items-center">
          <div>
            <p className="text-[10px] font-mono tracking-[0.15em] uppercase text-[#9d4edd]/40 mb-2">
              Pre-Launch Verification
            </p>
            <h2 className="text-xl md:text-[22px] font-bold text-gray-950 tracking-[-0.015em] mb-3">
              Readiness assessment before live validation
            </h2>
            <p className="text-[15px] text-gray-600 leading-[1.7] mb-4">
              Before entering live monitoring, the strategy must pass a
              readiness check based on optimization, cross-asset validation,
              and walk-forward testing.
            </p>

            <p className="text-[13px] text-gray-500 mb-3 font-medium">Readiness gates:</p>
            <div className="space-y-2 mb-6">
              {[
                'Stability score exceeds threshold',
                'Walk-forward performance consistency',
                'Parameter region validity',
                'Cross-asset validation integrity',
              ].map((item) => (
                <div key={item} className="flex items-start gap-2.5">
                  <span className="w-1 h-1 rounded-full bg-gray-400 mt-[7px] flex-shrink-0" />
                  <span className="text-[13px] text-gray-700 leading-snug">{item}</span>
                </div>
              ))}
            </div>

            <p className="text-[12px] text-[#9d4edd]/70 font-mono">
              All four gates must pass to enable live validation start
            </p>
          </div>

          <ReadinessChecksMock />
        </div>
      </section>

      {/* ════════════════ 4. LIVE PROGRESS + PARAMETERS ════════════════ */}
      <section className="py-16 px-4 sm:px-6 lg:px-10 bg-[#F9FAFB]">
        <div className="max-w-[1440px] mx-auto grid grid-cols-1 lg:grid-cols-[7fr_5fr] gap-12 lg:gap-16 items-center">
          <LiveProgressMock />

          <div>
            <p className="text-[10px] font-mono tracking-[0.15em] uppercase text-[#9d4edd]/40 mb-2">
              Progress Tracking
            </p>
            <h2 className="text-xl md:text-[22px] font-bold text-gray-950 tracking-[-0.015em] mb-3">
              Live data accumulation and parameter tracking
            </h2>
            <p className="text-[15px] text-gray-600 leading-[1.7] mb-4">
              As live candles accumulate, Adaptive Flow evaluates performance
              relative to the validated parameter region. The system tracks
              whether performance remains within expected statistical bounds.
            </p>
            <p className="text-[15px] text-gray-600 leading-[1.7] mb-4">
              The active parameters panel displays the current strategy
              parameters and when they were last optimized, giving immediate
              visibility into the state of the deployed configuration.
            </p>

            <p className="text-[12px] text-[#9d4edd]/70 font-mono">
              Panels: ReadinessOrProgressPanel + ActiveParametersPanel
            </p>
          </div>
        </div>
      </section>

      {/* ════════════════ 5. EQUITY CURVE + RISK TOLERANCE ════════════════ */}
      <section className="py-16 px-4 sm:px-6 lg:px-10 bg-white">
        <div className="max-w-[1440px] mx-auto grid grid-cols-1 lg:grid-cols-[5fr_7fr] gap-12 lg:gap-16 items-center">
          <div>
            <p className="text-[10px] font-mono tracking-[0.15em] uppercase text-[#9d4edd]/40 mb-2">
              Performance Envelopes
            </p>
            <h2 className="text-xl md:text-[22px] font-bold text-gray-950 tracking-[-0.015em] mb-3">
              Live performance vs validation baseline
            </h2>
            <p className="text-[15px] text-gray-600 leading-[1.7] mb-4">
              Live performance is continuously compared against the
              walk-forward validation baseline to detect early signs of
              structural drift. The chart phases in progressively as
              trade count grows.
            </p>
            <p className="text-[15px] text-gray-600 leading-[1.7] mb-4">
              The risk tolerance panel classifies drawdown behaviour using a
              gradient from Low to Extreme, with estimated recovery time and
              re-optimization cadence displayed alongside.
            </p>

            <p className="text-[13px] text-gray-500 mb-3 font-medium">Chart progression phases:</p>
            <div className="space-y-2 mb-6">
              {[
                'Phase 0: Empty chart — no live trades yet',
                'Phase 1 (1-9 trades): Expected p25-p75 band visible',
                'Phase 2 (10-29 trades): Band + expected median line added',
                'Phase 3 (30+ trades): Full comparison with live overlay',
              ].map((item) => (
                <div key={item} className="flex items-start gap-2.5">
                  <span className="w-1 h-1 rounded-full bg-gray-400 mt-[7px] flex-shrink-0" />
                  <span className="text-[13px] text-gray-700 leading-snug">{item}</span>
                </div>
              ))}
            </div>

            <p className="text-[12px] text-[#9d4edd]/70 font-mono">
              Panels: EquityCurveComparisonChart + RiskTolerancePanel
            </p>
          </div>

          <EquityCurveRiskMock />
        </div>
      </section>

      {/* ════════════════ 6. RIGHT SIDEBAR ════════════════ */}
      <section className="py-16 px-4 sm:px-6 lg:px-10 bg-[#F0F2F5]">
        <div className="max-w-[1440px] mx-auto grid grid-cols-1 lg:grid-cols-[7fr_5fr] gap-12 lg:gap-16 items-center">
          <RightSidebarMock />

          <div>
            <p className="text-[10px] font-mono tracking-[0.15em] uppercase text-[#9d4edd]/40 mb-2">
              Validation Context
            </p>
            <h2 className="text-xl md:text-[22px] font-bold text-gray-950 tracking-[-0.015em] mb-3">
              Operational status and research guidance panel
            </h2>
            <p className="text-[15px] text-gray-600 leading-[1.7] mb-4">
              The right sidebar provides validation context at a glance: WFA
              outcome, validation depth, out-of-sample performance metrics,
              and actionable improvement guidance. The research pipeline
              section shows exactly where the strategy sits in the
              optimization &rarr; WFA &rarr; Adaptive Flow workflow.
            </p>

            <p className="text-[13px] text-gray-500 mb-3 font-medium">Sidebar sections:</p>
            <div className="space-y-2 mb-6">
              {[
                'Validation outcome with confidence percentage',
                'Validation depth — windows passed vs minimum required',
                'Out-of-sample metrics (return, Sharpe, drawdown, WFE)',
                'Dynamic improvement guidance based on current state',
                'Research pipeline showing optimization lineage',
              ].map((item) => (
                <div key={item} className="flex items-start gap-2.5">
                  <span className="w-1 h-1 rounded-full bg-gray-400 mt-[7px] flex-shrink-0" />
                  <span className="text-[13px] text-gray-700 leading-snug">{item}</span>
                </div>
              ))}
            </div>

            <p className="text-[12px] text-[#9d4edd]/70 font-mono">
              Panel: AdaptiveFlowRightSidebar
            </p>
          </div>
        </div>
      </section>

      {/* ════════════════ 7. TRADES + MONITORING COVERAGE ════════════════ */}
      <section className="py-16 px-4 sm:px-6 lg:px-10 bg-[#F9FAFB]">
        <div className="max-w-[1440px] mx-auto grid grid-cols-1 lg:grid-cols-[5fr_7fr] gap-12 lg:gap-16 items-center">
          <div>
            <p className="text-[10px] font-mono tracking-[0.15em] uppercase text-[#9d4edd]/40 mb-2">
              Trade Diagnostics
            </p>
            <h2 className="text-xl md:text-[22px] font-bold text-gray-950 tracking-[-0.015em] mb-3">
              Trade diagnostics and monitoring visibility
            </h2>
            <p className="text-[15px] text-gray-600 leading-[1.7] mb-4">
              Every trade is logged and evaluated to provide detailed
              diagnostic visibility into strategy behaviour. Entry time,
              price, size, exit, PnL, and status are recorded for each
              trade as it occurs.
            </p>
            <p className="text-[15px] text-gray-600 leading-[1.7] mb-4">
              The monitoring coverage checklist confirms which automated
              detection systems are active: performance decay, Sharpe drift,
              drawdown expansion, and stability breach detection.
            </p>

            <p className="text-[12px] text-[#9d4edd]/70 font-mono">
              Panels: TradesTablePanel + Monitoring Coverage
            </p>
          </div>

          <TradesMonitoringMock />
        </div>
      </section>

      {/* ════════════════ 8. RE-OPTIMIZATION APPROVAL ════════════════ */}
      <section className="py-16 px-4 sm:px-6 lg:px-10 bg-white">
        <div className="max-w-[1440px] mx-auto grid grid-cols-1 lg:grid-cols-[7fr_5fr] gap-12 lg:gap-16 items-center">
          <ReOptApprovalMock />

          <div>
            <p className="text-[10px] font-mono tracking-[0.15em] uppercase text-[#9d4edd]/40 mb-2">
              Compliance-Gated Decisions
            </p>
            <h2 className="text-xl md:text-[22px] font-bold text-gray-950 tracking-[-0.015em] mb-3">
              Controlled re-optimization workflow
            </h2>
            <p className="text-[15px] text-gray-600 leading-[1.7] mb-4">
              When validation thresholds are breached, Adaptive Flow can
              recommend a re-optimization cycle. Researchers review the
              updated results before approving parameter changes.
            </p>
            <p className="text-[15px] text-gray-600 leading-[1.7] mb-4">
              Every decision &mdash; approve, defer, or reject &mdash; is
              logged with timestamp, notes, and current metrics for a complete
              audit trail.
            </p>

            <p className="text-[12px] text-[#9d4edd]/70 font-mono">
              Modal: ReOptimizationApprovalModal
            </p>
          </div>
        </div>
      </section>

      {/* ════════════════ 9. STATE TRANSITIONS ════════════════ */}
      <section className="py-16 px-4 sm:px-6 lg:px-10 bg-[#F9FAFB]">
        <div className="max-w-[960px] mx-auto">
          <div className="text-center mb-10">
            <h2 className="text-xl md:text-[24px] font-bold text-gray-950 tracking-[-0.015em] mb-5">
              Strategy health states
            </h2>
            <p className="text-[15px] text-gray-600 leading-[1.7] max-w-[600px] mx-auto">
              Adaptive Flow models strategy health as a set of monitoring
              states derived from validation metrics and statistical thresholds.
              Transitions happen automatically based on data-driven
              conditions.
            </p>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {[
              { label: 'Collecting', dot: 'bg-blue-400', desc: 'Building statistical baseline from incoming candles', animate: true },
              { label: 'Early Sample', dot: 'bg-blue-400', desc: 'Metrics forming but insufficient for confidence', animate: true },
              { label: 'Warmup', dot: 'bg-emerald-400', desc: 'Trade data accumulating toward minimum threshold', animate: true },
              { label: 'Active', dot: 'bg-emerald-400', desc: 'Full monitoring engaged, metrics confident', animate: true },
              { label: 'Warning', dot: 'bg-amber-400', desc: 'Deviation detected, monitoring heightened', animate: true },
              { label: 'Degrading', dot: 'bg-rose-400', desc: 'Confirmed decay, re-optimization recommended', animate: true },
              { label: 'Re-Optimizing', dot: 'bg-purple-400', desc: 'New parameters being computed', animate: true },
              { label: 'Stabilized', dot: 'bg-emerald-400', desc: 'Post-re-optimization, behavior stable again', animate: false },
            ].map((state) => (
              <div key={state.label} className="bg-white rounded-lg border border-gray-200/80 p-4 shadow-sm">
                <div className="flex items-center gap-2 mb-2">
                  <span className={`w-2 h-2 rounded-full ${state.dot} ${state.animate ? 'animate-pulse' : ''}`} />
                  <span className="text-sm font-semibold text-gray-900">{state.label}</span>
                </div>
                <p className="text-[12px] text-gray-500 leading-snug">{state.desc}</p>
              </div>
            ))}
          </div>

          <div className="mt-8 text-center">
            <p className="text-[12px] text-[#9d4edd]/70 font-mono">
              State machine: Collecting &rarr; Early Sample &rarr; Warmup &rarr; Active &rarr; Warning &rarr; Degrading &rarr; Re-Optimizing &rarr; Stabilized
            </p>
          </div>
        </div>
      </section>

      {/* ════════════════ 10. CONSOLE LOG ════════════════ */}
      <section className="py-16 px-4 sm:px-6 lg:px-10 bg-white">
        <div className="max-w-[1440px] mx-auto grid grid-cols-1 lg:grid-cols-[5fr_7fr] gap-12 lg:gap-16 items-center">
          <div>
            <p className="text-[10px] font-mono tracking-[0.15em] uppercase text-[#9d4edd]/40 mb-2">
              Operational Log
            </p>
            <h2 className="text-xl md:text-[22px] font-bold text-gray-950 tracking-[-0.015em] mb-3">
              Monitoring event stream
            </h2>
            <p className="text-[15px] text-gray-600 leading-[1.7] mb-4">
              A structured event stream records monitoring events,
              validation alerts, and state transitions for full
              auditability.
            </p>

            <p className="text-[12px] text-[#9d4edd]/70 font-mono">
              Panel: Console output (bottom dock)
            </p>
          </div>

          <ConsolePanel
            title="Adaptive Flow"
            context="BTCUSDT 1d -- EMA Crossover"
            icon={faHeartPulse}
            status="Running"
            lines={[
              { time: '09:00:00', type: 'info', source: 'engine', message: 'Live validation started -- connecting to price feed' },
              { time: '09:00:01', type: 'success', source: 'ws', message: 'Price feed connected -- BTCUSDT @ $67,160.71' },
              { time: '09:00:01', type: 'info', source: 'state', message: 'State: COLLECTING -- building statistical baseline' },
              { time: '09:01:00', type: 'info', source: 'candle', message: 'Candle processed: 2/720 -- no signal generated' },
              { time: '09:01:00', type: 'info', source: 'health', message: 'Health Score: 5.8 -- Confidence: 2% -- Trades: 0/5 min' },
              { time: '09:01:00', type: 'warning', source: 'warmup', message: 'Warmup incomplete: 0/3 conditions met (2-of-3 rule)' },
            ]}
          />
        </div>
      </section>

      {/* ════════════════ 11. CTA ════════════════ */}
      <section className="relative py-28 px-4 sm:px-6 lg:px-10 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-t from-[#9d4edd]/[0.03] to-transparent pointer-events-none" />
        <div className="max-w-[600px] mx-auto text-center relative z-10">
          <p className="text-[13px] text-gray-400/70 font-mono tracking-wide uppercase mb-4">
            Continuous validation completes the research pipeline.
          </p>
          <h2 className="text-xl md:text-[24px] font-bold text-white tracking-[-0.02em] mb-5">
            Strategies that monitor themselves
          </h2>
          <p className="text-[15px] text-gray-300/70 leading-[1.7] mb-3">
            A validated strategy must remain validated. Start building with
            continuous validation from day one.
          </p>
          <p className="text-[13px] text-gray-400/60 leading-[1.7] mb-10">
            Adaptive Flow runs as long as your strategy runs. No manual monitoring required.
          </p>
          <Link
            to="/pricing"
            className="inline-flex items-center gap-2.5 px-7 py-3.5 bg-[#9d4edd] hover:bg-[#8b3ec7] text-white rounded text-sm font-medium transition-colors"
          >
            Start Research
            <FontAwesomeIcon icon={faArrowRight} className="text-xs" />
          </Link>
          <div className="mt-4">
            <a href="#how-it-works" className="text-[13px] text-gray-400 hover:text-gray-200 transition-colors">
              View the Adaptive Flow lifecycle <span className="text-gray-600">&rarr;</span>
            </a>
          </div>
        </div>
      </section>

      {/* ════════════════ NAV FOOTER ════════════════ */}
      <section className="py-12 px-4 sm:px-6 lg:px-10 border-t border-gray-700/40">
        <div className="max-w-[960px] mx-auto flex items-center justify-between">
          <Link to="/features/optimization" className="inline-flex items-center gap-2 text-[13px] text-gray-500 hover:text-gray-300 transition-colors">
            <FontAwesomeIcon icon={faArrowLeft} className="text-[10px]" />
            Parameter Optimization
          </Link>
          <Link to="/pricing" className="inline-flex items-center gap-2 text-[13px] text-[#9d4edd] hover:text-[#b266ff] transition-colors font-medium">
            Start Research
            <FontAwesomeIcon icon={faArrowRight} className="text-[11px]" />
          </Link>
        </div>
      </section>

      <PublicFooter />
    </div>
  );
};

export default ContinuousValidationPage;
